import {isFeatureEnabled} from 'feature-flags';
import {DataStatus, FormControl, FormField, LabelActions, openDropdown} from 'platform/components';
import {Box, Grid, Show, Spinner} from 'platform/foundation';

import {useMemo} from 'react';
import {UseFormReturn, useWatch} from 'react-hook-form';

import {
  useGetContextTemplateListQuery,
  useRenderServiceOrderSmsTemplateMutation,
} from '@dms/api/core';
import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';

import {buildArray, RequiredTestIdProps, suffixTestId} from 'shared/web';

import {FormContactPerson} from '../../../hooks/useContactPersons';
import {EmailSmsNotificationForm} from '../types/EmailSmsNotificationForm';
import {TemplatesSmsDropdownContent} from './TemplatesSmsDropdownContent';

interface SmsSectionProps extends RequiredTestIdProps {
  control: FormControl<EmailSmsNotificationForm>;
  formApi: UseFormReturn<EmailSmsNotificationForm>;
  personsWithPhones: FormContactPerson[];
  orderId?: string;
  serviceCaseId: string;
}

export function SmsSection(props: SmsSectionProps) {
  const {setValue} = props.formApi;

  const enableSms = useWatch({control: props.control, name: 'sms.isEnabled'});
  const selectedPerson = useWatch({control: props.control, name: 'sms.data.person'});
  const selectedPhones = useWatch({control: props.control, name: 'sms.data.phone'});
  const {
    data: templates,
    isLoading: isLoadingTemplates,
    isError: isTemplatesError,
  } = useGetContextTemplateListQuery({
    type: 'sms',
    context: 'service-order',
  });
  const [renderTemplate, {isLoading: isRenderTemplateInProgress}] =
    useRenderServiceOrderSmsTemplateMutation();

  const availablePhones = useMemo(() => {
    const person = props.personsWithPhones.find((item) => item.value === selectedPerson);
    return getPhoneOptions(person ? [person] : [], selectedPhones);
  }, [props.personsWithPhones, selectedPerson, selectedPhones]);

  return (
    <DataStatus isLoading={isLoadingTemplates} isError={isTemplatesError}>
      <FormField
        control={props.control}
        type="switch"
        name="sms.isEnabled"
        label={i18n.t('entity.workshop.labels.enableSmsNotifications')}
        data-testid={suffixTestId('sms.isEnabled', props)}
      />
      <Show when={enableSms}>
        <Grid columns={2}>
          <FormField
            control={props.control}
            name="sms.data.person"
            type="choice"
            label={i18n.t('entity.businessCase.labels.contactPerson')}
            options={props.personsWithPhones}
            onChange={() => setValue('sms.data.phone', [])}
            data-testid={suffixTestId('sms.data.person', props)}
            isNotClearable
            isRequired
          />
          <FormField
            control={props.control}
            name="sms.data.phone"
            type="creatableMultiChoice"
            label={i18n.t('entity.person.labels.phoneNumber')}
            data-testid={suffixTestId('sms.data.phone', props)}
            options={availablePhones}
            isNotClearable
            isRequired
          />
        </Grid>
        <Box position="relative">
          <Show when={isRenderTemplateInProgress}>
            <Spinner variant="overlay" />
          </Show>
          <FormField
            control={props.control}
            type="textarea"
            name="sms.data.message"
            minRows={5}
            label={i18n.t('general.labels.message')}
            data-testid={suffixTestId('sms.data.message', props)}
            labelActions={buildArray<LabelActions[number]>().when(
              isFeatureEnabled(featureFlags.MESSAGE_TEMPLATES) && props.orderId,
              {
                leftIcon: 'content/add_circle',
                title: i18n.t('general.labels.templates'),
                onClick: (e) =>
                  openDropdown(
                    e,
                    <TemplatesSmsDropdownContent
                      formApi={props.formApi}
                      control={props.control}
                      renderTemplate={renderTemplate}
                      templates={templates}
                      serviceCaseId={props.serviceCaseId}
                      serviceOrderId={props.orderId || ' '}
                    />,
                    {
                      isHeightLimited: true,
                    }
                  ),
              }
            )}
            isRequired
            isResizable
          />
        </Box>
      </Show>
    </DataStatus>
  );
}

function getPhoneOptions(persons: FormContactPerson[], currentValues: string[] = []) {
  const personsPhones =
    persons?.flatMap((p) =>
      (p.phoneNumbers ?? []).map((ph) =>
        ph && ph.value ? {value: ph.value, label: ph.value} : undefined
      )
    ) ?? [];

  const personsPhonesValues = new Set(personsPhones.map((e) => e?.value));
  const currentValueOptions = (currentValues ?? [])
    .filter((val) => val && !personsPhonesValues.has(val))
    .map((val) => ({value: val, label: val}));

  return [...personsPhones, ...currentValueOptions].filter(Boolean);
}
